import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { 
  UserIcon, 
  CalendarIcon, 
  ClockIcon, 
  BeakerIcon, 
  HeartIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  DocumentMagnifyingGlassIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import AppointmentBookingModal from './AppointmentBookingModal';

// New EncounterDocumentsModal component
const EncounterDocumentsModal = ({ isOpen, onClose, encounter, patientId }) => {
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState('');
  const [docContentType, setDocContentType] = useState('');

  useEffect(() => {
    if (isOpen && encounter && patientId) {
      setLoading(true);
      setError('');
      setSelectedDoc(null);
      setDocContent('');
      setDocContentType('');
      axios.get(`${API_URL}/api/documents/${patientId}?encounterId=${encounter.id}`)
        .then(res => {
          if (res.data && res.data.entry) {
            setDocuments(res.data.entry.map(entry => entry.resource));
          } else {
            setDocuments([]);
          }
        })
        .catch(err => {
          setError('Failed to fetch documents for this encounter.');
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, encounter, patientId]);

  const handleViewDocument = async (doc) => {
    setSelectedDoc(doc);
    setDocContent('');
    setDocContentType('');
    setLoading(true);
    try {
      // Find the attachment with HTML content type first, then fallback to first attachment
      const htmlAttachment = doc.content?.find(att => att.attachment?.contentType === 'text/html');
      const attachment = htmlAttachment || doc.content?.[0];
      
      if (attachment && attachment.attachment?.url) {
        // Extract the binary ID from the URL (format: "Binary/id")
        const binaryId = attachment.attachment.url.replace('Binary/', '');
        const url = `${API_URL}/api/binary/${binaryId}`;
        const res = await axios.get(url, {
          params: {
            contentType: attachment.attachment.contentType
          }
        });
        
        if (attachment.attachment.contentType === 'text/html') {
          setDocContent(res.data.data || res.data);
          setDocContentType('html');
        } else {
          setDocContent(res.data.data || res.data);
          setDocContentType(attachment.attachment.contentType || 'text/plain');
        }
      } else {
        setError('No viewable content for this document.');
      }
    } catch (err) {
      setError('Failed to load document content.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentDetails = async (documentId) => {
    try {
      const response = await axios.get(`${API_URL}/api/document/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching document details:', error);
      throw error;
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // First get the full document details (same as main app)
      const documentId = doc.id;
      const fullDocument = await fetchDocumentDetails(documentId);
      
      // Check if we have the specified attachment
      const attachment = fullDocument?.content?.[0]?.attachment;
      if (!attachment?.url) {
        setError("No attachment found for this document");
        return;
      }

      // Extract binary ID from URL (assuming format contains "Binary/[id]")
      const binaryMatch = attachment.url.match(/Binary\/([^/]+)/);
      if (!binaryMatch) {
        setError("Invalid binary URL format");
        return;
      }

      const binaryId = binaryMatch[1];
      
      // Get content type from attachment for Cerner compatibility
      const attachmentContentType = attachment.contentType;
      
      // Fetch binary content with content type parameter for Cerner
      const response = await axios.get(`${API_URL}/api/binary/${binaryId}`, {
        params: {
          contentType: attachmentContentType
        }
      });
      
      // Get the content type and determine file extension
      const contentType = response.data.contentType;
      let data = response.data.data;
      let extension;
      
      // Determine file extension based on content type
      if (contentType.includes('text/html')) {
        extension = 'html';
      } else if (contentType.includes('text/rtf')) {
        extension = 'rtf';
      } else if (contentType.includes('application/pdf')) {
        extension = 'pdf';
      } else if (contentType.includes('image/jpeg')) {
        extension = 'jpg';
      } else if (contentType.includes('image/png')) {
        extension = 'png';
      } else if (contentType.includes('image/gif')) {
        extension = 'gif';
      } else if (contentType.includes('text/plain')) {
        extension = 'txt';
      } else {
        extension = contentType.split('/')[1] || 'bin';
      }
      
      // Get filename from attachment or generate one
      const fileName = attachment.title || attachment.filename || `document.${extension}`;
      
      // Handle different data formats
      let blob;
      if (typeof data === 'string') {
        // For text-based content (HTML, RTF, etc.)
        blob = new Blob([data], { type: contentType });
      } else if (data && typeof data === 'object' && data.raw) {
        // For binary content with raw array data (new format)
        const uint8Array = new Uint8Array(data.raw);
        blob = new Blob([uint8Array], { type: contentType });
      } else if (typeof data === 'string') {
        // For binary content with base64 string (legacy format)
        const binData = atob(data);
        blob = new Blob([binData], { type: contentType });
      } else {
        // Fallback for unknown data format
        console.warn('Unknown data format:', typeof data, data);
        blob = new Blob([data], { type: contentType });
      }
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      setError(error.message);
    }
  };

  if (!isOpen || !encounter) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Encounter Documents</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <XCircleIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{encounter.type?.[0]?.text || encounter.type?.[0]?.coding?.[0]?.display || 'Encounter'} ({new Date(encounter.period?.start).toLocaleDateString()})</h3>
          {error && <div className="mb-4 text-red-600">{error}</div>}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-600"></div>
              <span className="ml-2 text-gray-600">Loading documents...</span>
            </div>
          ) : selectedDoc && docContentType === 'html' ? (
            <div>
              <button onClick={() => setSelectedDoc(null)} className="mb-4 text-blue-600 hover:underline">← Back to Documents</button>
              <div className="border rounded-lg p-4 bg-gray-50 max-h-[60vh] overflow-auto">
                <div dangerouslySetInnerHTML={{ __html: docContent }} />
              </div>
            </div>
          ) : selectedDoc ? (
            <div>
              <button onClick={() => setSelectedDoc(null)} className="mb-4 text-blue-600 hover:underline">← Back to Documents</button>
              <div className="border rounded-lg p-4 bg-gray-50 max-h-[60vh] overflow-auto">
                <pre className="whitespace-pre-wrap break-words text-xs">{docContent}</pre>
              </div>
            </div>
          ) : (
            <div>
              {documents.length === 0 ? (
                <div className="text-gray-500">No documents found for this encounter.</div>
              ) : (
                <ul className="space-y-3">
                  {documents.map((doc, idx) => {
                    const attachment = doc.content?.[0]?.attachment;
                    const contentType = attachment?.contentType || 'unknown';
                    const isViewable = contentType === 'text/html';
                    
                    return (
                      <li key={doc.id || idx} className="border rounded-lg p-3 bg-cyan-50 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{doc.type?.text || doc.type?.coding?.[0]?.display || 'Document'}</div>
                          <div className="text-xs text-gray-600">Date: {doc.date ? new Date(doc.date).toLocaleString() : 'Unknown'}</div>
                          <div className="text-xs text-gray-600">Status: {doc.status}</div>
                          <div className="text-xs text-gray-500">Type: {contentType}</div>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          {isViewable && (
                            <button
                              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                              onClick={() => handleViewDocument(doc)}
                            >
                              View
                            </button>
                          )}
                          <button
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                            onClick={() => handleDownloadDocument(doc)}
                          >
                            Download
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PatientDashboard = () => {
  const { patientId } = useParams();
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [loadingEncounters, setLoadingEncounters] = useState(false);
  const [immunizations, setImmunizations] = useState([]);
  const [loadingImmunizations, setLoadingImmunizations] = useState(false);
  const [procedures, setProcedures] = useState([]);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loadingServiceRequests, setLoadingServiceRequests] = useState(false);
  const [medications, setMedications] = useState([]);
  const [loadingMedications, setLoadingMedications] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [vitalSigns, setVitalSigns] = useState([]);
  const [loadingVitalSigns, setLoadingVitalSigns] = useState(false);
  const [labResults, setLabResults] = useState([]);
  const [loadingLabResults, setLoadingLabResults] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showEncounterDocsModal, setShowEncounterDocsModal] = useState(false);
  const [selectedEncounter, setSelectedEncounter] = useState(null);

  // Mock patient data as fallback
  const mockPatientData = {
    demographics: {
      name: "Sarah Johnson",
      age: 34,
      gender: "Female",
      mrn: "MRN123456789",
      dateOfBirth: "1990-03-15",
      phone: "(555) 123-4567",
      email: "sarah.johnson@email.com",
      address: "123 Main St, Charlotte, NC 28202",
      emergencyContact: "Michael Johnson (Spouse) - (555) 987-6543",
      primaryPhysician: "Dr. Sarah Smith"
    },
    prescriptions: {
      active: 5,
      total: 8,
      medications: [
        { name: "Lisinopril", dosage: "10mg", frequency: "Daily", status: "active", refills: 2 },
        { name: "Metformin", dosage: "500mg", frequency: "Twice daily", status: "active", refills: 1 },
        { name: "Atorvastatin", dosage: "20mg", frequency: "Daily", status: "active", refills: 3 },
        { name: "Omeprazole", dosage: "40mg", frequency: "Daily", status: "active", refills: 0 },
        { name: "Vitamin D3", dosage: "2000 IU", frequency: "Daily", status: "active", refills: 5 }
      ]
    },
    appointments: {
      recent: [
        { date: "2024-01-15", type: "Primary Care", provider: "Dr. Smith", status: "completed" },
        { date: "2024-01-08", type: "Cardiology", provider: "Dr. Wilson", status: "completed" },
        { date: "2024-01-02", type: "Lab Work", provider: "LabCorp", status: "completed" }
      ],
      upcoming: [
        { date: "2024-03-15", type: "Eye Exam", provider: "Dr. Brown", status: "scheduled" },
        { date: "2024-03-05", type: "Dental Cleaning", provider: "Dr. Davis", status: "scheduled" },
        { date: "2024-02-20", type: "Primary Care", provider: "Dr. Smith", status: "scheduled" }
      ]
    },
    procedures: {
      scheduled: [
        { date: "2024-04-10", type: "Mammogram", provider: "Dr. Lee", location: "Imaging Center" },
        { date: "2024-02-28", type: "Colonoscopy", provider: "Dr. Garcia", location: "Main Hospital" }
      ]
    },
    vaccinations: {
      upToDate: 85,
      recent: [
        { name: "COVID-19 Booster", date: "2023-12-15", status: "completed" },
        { name: "Flu Shot", date: "2023-10-20", status: "completed" },
        { name: "Tdap", date: "2023-08-10", status: "completed" }
      ],
      due: [
        { name: "Pneumococcal", dueDate: "2024-06-15" },
        { name: "Hepatitis B", dueDate: "2024-08-20" }
      ]
    },
    vitals: {
      bloodPressure: "120/80",
      heartRate: 72,
      temperature: 98.6,
      weight: "145 lbs",
      height: "5'6\"",
      bmi: 23.4
    }
  };

  // Fetch patient data from API
  useEffect(() => {
    const fetchPatientData = async () => {
      if (!patientId) {
        // If no patient ID, use mock data
        setPatientData(mockPatientData);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch patient information
        const patientResponse = await axios.get(`${API_URL}/api/patient/${patientId}`);
        const patient = patientResponse.data;

        // Extract patient demographics
        const demographics = {
          name: patient.name?.[0]?.text || 
                `${patient.name?.[0]?.given?.join(' ') || ''} ${patient.name?.[0]?.family || ''}`.trim() ||
                'Unknown Name',
          age: patient.birthDate ? 
               Math.floor((new Date() - new Date(patient.birthDate)) / (365.25 * 24 * 60 * 60 * 1000)) : 
               null,
          gender: patient.gender || 'Unknown',
          mrn: patient.identifier?.find(id => id.type?.coding?.[0]?.code === 'MR')?.value || 
               patient.identifier?.[0]?.value || 
               'No MRN',
          dateOfBirth: patient.birthDate || 'Unknown',
          phone: patient.telecom?.find(t => t.system === 'phone')?.value || 'No phone',
          email: patient.telecom?.find(t => t.system === 'email')?.value || 'No email',
          address: patient.address?.[0] ? 
                   `${patient.address[0].line?.[0] || ''}, ${patient.address[0].city || ''}, ${patient.address[0].state || ''} ${patient.address[0].postalCode || ''}`.replace(/^,\s*/, '').replace(/,\s*$/, '') : 
                   'No address',
          emergencyContact: patient.contact?.[0] ? 
                           `${patient.contact[0].name?.text || 'Unknown'} (${patient.contact[0].relationship?.[0]?.text || 'Emergency Contact'}) - ${patient.contact[0].telecom?.[0]?.value || 'No phone'}` : 
                           'No emergency contact'
        };

        // Try to fetch encounters to get primary physician
        let primaryPhysician = 'Not specified';
        try {
          const encountersResponse = await axios.get(`${API_URL}/api/encounters/${patientId}`);
          if (encountersResponse.data && encountersResponse.data.entry && encountersResponse.data.entry.length > 0) {
            // Get the most recent encounter with a practitioner
            const recentEncounter = encountersResponse.data.entry
              .filter(enc => enc.resource.participant?.some(p => p.individual?.reference?.includes('Practitioner')))
              .sort((a, b) => new Date(b.resource.period?.start || 0) - new Date(a.resource.period?.start || 0))[0];
            
            if (recentEncounter) {
              const practitionerRef = recentEncounter.resource.participant?.find(p => p.individual?.reference?.includes('Practitioner'));
              if (practitionerRef) {
                const practitionerId = practitionerRef.individual.reference.split('/')[1];
                const practitionerResponse = await axios.get(`${API_URL}/api/practitioner/${practitionerId}`);
                const practitioner = practitionerResponse.data;
                primaryPhysician = practitioner.name?.[0]?.text || 
                                  `${practitioner.name?.[0]?.prefix?.join(' ') || ''} ${practitioner.name?.[0]?.given?.join(' ') || ''} ${practitioner.name?.[0]?.family || ''}`.trim() ||
                                  'Unknown Practitioner';
              }
            }
          }
        } catch (encounterError) {
          console.error('Error fetching encounters:', encounterError);
        }

        // Update demographics with primary physician
        const updatedDemographics = {
          ...demographics,
          primaryPhysician
        };

        // For now, use mock data for other sections but update demographics
        const updatedPatientData = {
          ...mockPatientData,
          demographics: updatedDemographics
        };

        setPatientData(updatedPatientData);
      } catch (err) {
        console.error('Error fetching patient data:', err);
        setError(err.response?.data?.error || err.message || 'Failed to fetch patient data');
        // Fall back to mock data if API fails
        setPatientData(mockPatientData);
      } finally {
        setLoading(false);
      }
    };

    const fetchEncounters = async () => {
      if (!patientId) {
        setEncounters([]);
        return;
      }

      try {
        setLoadingEncounters(true);
        const encountersResponse = await axios.get(`${API_URL}/api/encounters/${patientId}`);
        
        if (encountersResponse.data && encountersResponse.data.entry) {
          // Sort encounters by date (most recent first)
          const sortedEncounters = encountersResponse.data.entry
            .map(entry => entry.resource)
            .sort((a, b) => new Date(b.period?.start || 0) - new Date(a.period?.start || 0));
          
          setEncounters(sortedEncounters);
        } else {
          setEncounters([]);
        }
      } catch (err) {
        console.error('Error fetching encounters:', err);
        setEncounters([]);
      } finally {
        setLoadingEncounters(false);
      }
    };

    const fetchImmunizations = async () => {
      if (!patientId) {
        setImmunizations([]);
        return;
      }

      try {
        setLoadingImmunizations(true);
        const immunizationsResponse = await axios.get(`${API_URL}/api/immunizations/${patientId}`);
        
        if (immunizationsResponse.data && immunizationsResponse.data.entry) {
          // Sort immunizations by date (most recent first)
          const sortedImmunizations = immunizationsResponse.data.entry
            .map(entry => entry.resource)
            .sort((a, b) => new Date(b.occurrenceDateTime || b.occurrenceString || 0) - new Date(a.occurrenceDateTime || a.occurrenceString || 0));
          
          setImmunizations(sortedImmunizations);
        } else {
          setImmunizations([]);
        }
      } catch (err) {
        console.error('Error fetching immunizations:', err);
        setImmunizations([]);
      } finally {
        setLoadingImmunizations(false);
      }
    };

    const fetchProcedures = async () => {
      if (!patientId) {
        setProcedures([]);
        return;
      }

      try {
        setLoadingProcedures(true);
        const proceduresResponse = await axios.get(`${API_URL}/api/procedures/${patientId}`);
        
        if (proceduresResponse.data && proceduresResponse.data.entry) {
          // Sort procedures by date (most recent first)
          const sortedProcedures = proceduresResponse.data.entry
            .map(entry => entry.resource)
            .sort((a, b) => new Date(b.performedDateTime || b.performedPeriod?.start || 0) - new Date(a.performedDateTime || a.performedPeriod?.start || 0));
          
          setProcedures(sortedProcedures);
        } else {
          setProcedures([]);
        }
      } catch (err) {
        console.error('Error fetching procedures:', err);
        setProcedures([]);
      } finally {
        setLoadingProcedures(false);
      }
    };

    const fetchServiceRequests = async () => {
      if (!patientId) {
        setServiceRequests([]);
        return;
      }

      try {
        setLoadingServiceRequests(true);
        const serviceRequestsResponse = await axios.get(`${API_URL}/api/service-requests/${patientId}`);
        
        if (serviceRequestsResponse.data && serviceRequestsResponse.data.entry) {
          // Sort service requests by date (most recent first)
          const sortedServiceRequests = serviceRequestsResponse.data.entry
            .map(entry => entry.resource)
            .sort((a, b) => new Date(b.authoredOn || 0) - new Date(a.authoredOn || 0));
          
          setServiceRequests(sortedServiceRequests);
        } else {
          setServiceRequests([]);
        }
      } catch (err) {
        console.error('Error fetching service requests:', err);
        setServiceRequests([]);
      } finally {
        setLoadingServiceRequests(false);
      }
    };

    const fetchMedications = async () => {
      if (!patientId) {
        setMedications([]);
        return;
      }

      try {
        setLoadingMedications(true);
        // Fetch all medications without status filter to get all types
        const medicationsResponse = await axios.get(`${API_URL}/api/medications/${patientId}`);
        
        if (medicationsResponse.data && medicationsResponse.data.entry) {
          // Sort medications by date (most recent first)
          const sortedMedications = medicationsResponse.data.entry
            .map(entry => entry.resource)
            .sort((a, b) => new Date(b.authoredOn || 0) - new Date(a.authoredOn || 0));
          
          setMedications(sortedMedications);
        } else {
          setMedications([]);
        }
      } catch (err) {
        console.error('Error fetching medications:', err);
        setMedications([]);
      } finally {
        setLoadingMedications(false);
      }
    };

    const fetchAppointments = async () => {
      if (!patientId) {
        setAppointments([]);
        return;
      }

      try {
        setLoadingAppointments(true);
        
        // For Cerner, we need to include a date parameter (last 4 years)
        const fourYearsAgo = new Date();
        fourYearsAgo.setFullYear(fourYearsAgo.getFullYear() - 4);
        const dateParam = fourYearsAgo.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        
        const appointmentsResponse = await axios.get(`${API_URL}/api/appointments/${patientId}?date=${dateParam}`);
        
        if (appointmentsResponse.data && appointmentsResponse.data.entry) {
          // Sort appointments by date (most recent first)
          const sortedAppointments = appointmentsResponse.data.entry
            .map(entry => entry.resource)
            .sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0)); // Sort by start time, most recent first
          
          setAppointments(sortedAppointments);
        } else {
          setAppointments([]);
        }
      } catch (err) {
        console.error('Error fetching appointments:', err);
        setAppointments([]);
      } finally {
        setLoadingAppointments(false);
      }
    };

    const fetchVitalSigns = async () => {
      if (!patientId) {
        setVitalSigns([]);
        return;
      }

      try {
        setLoadingVitalSigns(true);
        // Fetch vital signs observations specifically
        const vitalSignsResponse = await axios.get(`${API_URL}/api/observations/${patientId}?category=vital-signs`);
        
        if (vitalSignsResponse.data && vitalSignsResponse.data.entry) {
          // Sort vital signs by date (most recent first)
          const sortedVitalSigns = vitalSignsResponse.data.entry
            .map(entry => entry.resource)
            .sort((a, b) => new Date(b.effectiveDateTime || 0) - new Date(a.effectiveDateTime || 0));
          
          setVitalSigns(sortedVitalSigns);
        } else {
          setVitalSigns([]);
        }
      } catch (err) {
        console.error('Error fetching vital signs:', err);
        setVitalSigns([]);
      } finally {
        setLoadingVitalSigns(false);
      }
    };

    const fetchLabResults = async () => {
      if (!patientId) {
        setLabResults([]);
        return;
      }

      try {
        setLoadingLabResults(true);
        // Fetch laboratory observations specifically
        const labResultsResponse = await axios.get(`${API_URL}/api/observations/${patientId}?category=laboratory`);
        
        if (labResultsResponse.data && labResultsResponse.data.entry) {
          // Sort lab results by date (most recent first)
          const sortedLabResults = labResultsResponse.data.entry
            .map(entry => entry.resource)
            .sort((a, b) => new Date(b.effectiveDateTime || 0) - new Date(a.effectiveDateTime || 0));
          
          setLabResults(sortedLabResults);
        } else {
          setLabResults([]);
        }
      } catch (err) {
        console.error('Error fetching lab results:', err);
        setLabResults([]);
      } finally {
        setLoadingLabResults(false);
      }
    };

    fetchPatientData();
    fetchEncounters();
    fetchImmunizations();
    fetchProcedures();
    fetchServiceRequests();
    fetchMedications();
    fetchAppointments();
    fetchVitalSigns();
    fetchLabResults();
  }, [patientId]);

  // Handle verification button click
  const handleVerification = async () => {
    if (!isVerified) {
      const verificationData = {
        patientId: patientId || 'unknown',
        timestamp: new Date().toISOString(),
        action: 'verified',
        userAgent: navigator.userAgent
      };

      try {
        // Log to local file via API endpoint
        await axios.post(`${API_URL}/api/log-verification`, verificationData);
        setIsVerified(true);
      } catch (error) {
        console.error('Error logging verification:', error);
        // Still set as verified even if logging fails
        setIsVerified(true);
      }
    }
  };

  // Circular progress component
  const CircularProgress = ({ percentage, size = 120, strokeWidth = 8, color = "blue" }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    const colorClasses = {
      blue: "text-blue-500",
      green: "text-green-500",
      yellow: "text-yellow-500",
      red: "text-red-500",
      purple: "text-purple-500"
    };

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="transparent"
            className="text-gray-200"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            className={`${colorClasses[color]} transition-all duration-300 ease-in-out`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-lg font-bold ${colorClasses[color]}`}>
            {percentage}%
          </span>
        </div>
      </div>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-lg text-gray-600">Loading patient data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !patientData && patientId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-red-800">Error Loading Patient Data</h3>
            </div>
            <p className="text-red-700 mt-2">{error}</p>
            <p className="text-red-600 mt-1">Patient ID: {patientId}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">

        {/* Top Row - Patient Info and Appointments */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          
          {/* Patient Info Card - Takes 2 columns */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-full mr-4">
                  <UserIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{patientData.demographics.name}</h2>
                  <p className="text-gray-600">MRN: {patientData.demographics.mrn}</p>
                </div>
              </div>
                          <div className="flex items-center space-x-3">
              <button
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-2" />
                Epic
              </button>
              {isVerified ? (
                <div className="flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-lg">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span className="font-semibold">Verified</span>
                </div>
              ) : (
                <button
                  onClick={handleVerification}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center"
                >
                  <ShieldCheckIcon className="h-5 w-5 mr-2" />
                  Verify
                </button>
              )}
            </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 font-medium">Age</p>
                <p className="text-base font-semibold text-gray-900">{patientData.demographics.age || 'Unknown'} {patientData.demographics.age ? 'years' : ''}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 font-medium">Gender</p>
                <p className="text-base font-semibold text-gray-900">{patientData.demographics.gender}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 font-medium">Date of Birth</p>
                <p className="text-base font-semibold text-gray-900">{patientData.demographics.dateOfBirth}</p>
              </div>
            </div>
            
            {/* Contact Information Card */}
            <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-600 font-medium">Phone</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{patientData.demographics.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium">Email</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{patientData.demographics.email || 'Not provided'}</p>
                </div>
              </div>
            </div>
            
            {/* Medical Information Card */}
            <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Medical Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-600 font-medium">Primary Physician</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{patientData.demographics.primaryPhysician || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium">Emergency Contact</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{patientData.demographics.emergencyContact || 'Not provided'}</p>
                </div>
              </div>
            </div>
            
            {/* Address Card */}
            <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Address
              </h4>
              <p className="text-sm font-semibold text-gray-900">{patientData.demographics.address || 'Not provided'}</p>
            </div>
          </div>

          {/* Appointments Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-purple-100 p-3 rounded-full mr-3">
                  <CalendarIcon className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Appointments & Surgeries</h3>
              </div>
              <button
                onClick={() => setShowAppointmentModal(true)}
                className="bg-purple-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                Schedule
              </button>
            </div>
            <div className="space-y-3">
              {loadingAppointments ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                  <span className="ml-2 text-gray-600">Loading appointments...</span>
                </div>
              ) : appointments.length > 0 ? (
                <div className="max-h-96 overflow-y-auto pr-2">
                  {appointments.map((appointment, index) => {
                    const appointmentDate = appointment.start ? 
                      new Date(appointment.start).toLocaleDateString() : 
                      'Unknown date';
                    const appointmentTime = appointment.start ? 
                      new Date(appointment.start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                      '';
                    const appointmentEndTime = appointment.end ? 
                      new Date(appointment.end).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                      '';
                    const appointmentName = appointment.serviceType?.[0]?.text || 
                      appointment.serviceType?.[0]?.coding?.[0]?.display || 
                      appointment.description || 
                      'Unknown appointment type';
                    const appointmentStatus = appointment.status || 'Unknown';
                    const practitioner = appointment.participant?.find(p => p.actor?.reference?.includes('Practitioner'))?.actor?.display || 
                      appointment.participant?.find(p => p.actor?.reference?.includes('Practitioner'))?.actor?.reference || 
                      'No practitioner assigned';
                    const location = appointment.participant?.find(p => p.actor?.reference?.includes('Location'))?.actor?.display || 
                      appointment.participant?.find(p => p.actor?.reference?.includes('Location'))?.actor?.reference || 
                      'No location assigned';
                    const isSurgery = appointment.serviceType?.[0]?.coding?.some(coding => 
                      coding.code?.includes('surgery') || 
                      coding.display?.toLowerCase().includes('surgery') ||
                      appointment.description?.toLowerCase().includes('surgery')
                    ) || false;
                    
                    return (
                      <div key={appointment.id || index} className={`p-4 rounded-lg border mb-3 ${
                        isSurgery ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <h4 className="font-semibold text-gray-900">{appointmentName}</h4>
                            {isSurgery && (
                              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Surgery
                              </span>
                            )}
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            appointmentStatus === 'booked' ? 'bg-green-100 text-green-800' :
                            appointmentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            appointmentStatus === 'cancelled' ? 'bg-red-100 text-red-800' :
                            appointmentStatus === 'noshow' ? 'bg-gray-100 text-gray-800' :
                            appointmentStatus === 'entered-in-error' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {appointmentStatus}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <div>
                            <p className="text-sm text-gray-600">Date: {appointmentDate}</p>
                            <p className="text-sm text-gray-600">Time: {appointmentTime}{appointmentEndTime ? ` - ${appointmentEndTime}` : ''}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Provider: {practitioner}</p>
                            <p className="text-sm text-gray-600">Location: {location}</p>
                          </div>
                        </div>
                        {appointment.reasonCode?.[0]?.text && (
                          <p className="text-xs text-gray-500 mt-1">Reason: {appointment.reasonCode[0].text}</p>
                        )}
                        {appointment.note?.[0]?.text && (
                          <p className="text-xs text-gray-500 mt-1">Notes: {appointment.note[0].text}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No appointments found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Dashboard Grid - 3 cards per row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          
          {/* Medications Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center mb-6">
              <div className="bg-green-100 p-3 rounded-full mr-3">
                <BeakerIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Medications</h3>
            </div>
            <div className="space-y-3">
              {loadingMedications ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                  <span className="ml-2 text-gray-600">Loading medications...</span>
                </div>
              ) : medications.length > 0 ? (
                <div className="max-h-96 overflow-y-auto pr-2">
                  {medications.map((medication, index) => {
                    const medicationName = medication.medicationCodeableConcept?.text || 
                      medication.medicationCodeableConcept?.coding?.[0]?.display || 
                      'Unknown medication';
                    const medicationStatus = medication.status || 'Unknown';
                    const dosage = medication.dosageInstruction?.[0]?.text || 
                      medication.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity?.value + ' ' + 
                      medication.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity?.unit || 
                      'No dosage info';
                    const frequency = medication.dosageInstruction?.[0]?.timing?.repeat?.frequency + 'x ' + 
                      medication.dosageInstruction?.[0]?.timing?.repeat?.periodUnit || 
                      medication.dosageInstruction?.[0]?.timing?.code?.text || 
                      'No frequency info';
                    const prescriber = medication.requester?.display || 'Unknown prescriber';
                    const date = medication.authoredOn ? 
                      new Date(medication.authoredOn).toLocaleDateString() : 
                      'Unknown date';
                    const refills = medication.dispenseRequest?.numberOfRepeatsAllowed || 0;
                    
                    return (
                      <div key={medication.id || index} className="p-4 bg-green-50 rounded-lg border border-green-200 mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{medicationName}</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            medicationStatus === 'active' ? 'bg-green-100 text-green-800' :
                            medicationStatus === 'stopped' ? 'bg-red-100 text-red-800' :
                            medicationStatus === 'on-hold' ? 'bg-yellow-100 text-yellow-800' :
                            medicationStatus === 'completed' ? 'bg-blue-100 text-blue-800' :
                            medicationStatus === 'entered-in-error' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {medicationStatus}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <div>
                            <p className="text-sm text-gray-600">Dosage: {dosage}</p>
                            <p className="text-sm text-gray-600">Frequency: {frequency}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Prescriber: {prescriber}</p>
                            <p className="text-sm text-gray-600">Date: {date}</p>
                          </div>
                        </div>
                        {refills > 0 && (
                          <p className="text-xs text-gray-500">Refills remaining: {refills}</p>
                        )}
                        {medication.reasonCode?.[0]?.text && (
                          <p className="text-xs text-gray-500 mt-1">Reason: {medication.reasonCode[0].text}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BeakerIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No medications found</p>
                </div>
              )}
            </div>
          </div>

          {/* Encounters Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center mb-6">
              <div className="bg-cyan-100 p-3 rounded-full mr-3">
                <UserGroupIcon className="h-6 w-6 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Recent Encounters</h3>
            </div>
            <div className="space-y-3">
              {loadingEncounters ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-600"></div>
                  <span className="ml-2 text-gray-600">Loading encounters...</span>
                </div>
              ) : encounters.length > 0 ? (
                <div className="max-h-96 overflow-y-auto pr-2">
                  {encounters.map((encounter, index) => {
                    const encounterDate = encounter.period?.start ? new Date(encounter.period.start).toLocaleDateString() : 'Unknown date';
                    const encounterType = encounter.type?.[0]?.text || encounter.type?.[0]?.coding?.[0]?.display || 'Unknown type';
                    const encounterStatus = encounter.status || 'Unknown';
                    const practitioner = encounter.participant?.find(p => p.individual?.reference?.includes('Practitioner'));
                    const practitionerName = practitioner ? 'Dr. ' + (practitioner.individual?.display || 'Unknown') : 'No practitioner';
                    
                    return (
                      <div
                        key={encounter.id || index}
                        className="p-4 bg-cyan-50 rounded-lg border border-cyan-200 cursor-pointer hover:bg-cyan-100 transition-colors"
                        onClick={() => { setSelectedEncounter(encounter); setShowEncounterDocsModal(true); }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{encounterType}</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            encounterStatus === 'finished' ? 'bg-green-100 text-green-800' :
                            encounterStatus === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                            encounterStatus === 'planned' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {encounterStatus}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{practitionerName}</p>
                        <p className="text-sm font-semibold text-gray-900">{encounterDate}</p>
                        {encounter.reasonCode?.[0]?.text && (
                          <p className="text-xs text-gray-500 mt-1">Reason: {encounter.reasonCode[0].text}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <UserGroupIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No encounters found</p>
                </div>
              )}
            </div>
          </div>

          {/* Procedures Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center mb-6">
              <div className="bg-orange-100 p-3 rounded-full mr-3">
                <HeartIcon className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Procedures & Orders</h3>
            </div>
            <div className="space-y-3">
              {loadingProcedures || loadingServiceRequests ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                  <span className="ml-2 text-gray-600">Loading procedures...</span>
                </div>
              ) : (procedures.length > 0 || serviceRequests.length > 0) ? (
                <div className="max-h-96 overflow-y-auto pr-2">
                  {/* Display Procedures */}
                  {procedures.map((procedure, index) => {
                    const procedureDate = procedure.performedDateTime ? 
                      new Date(procedure.performedDateTime).toLocaleDateString() : 
                      procedure.performedPeriod?.start ? 
                      new Date(procedure.performedPeriod.start).toLocaleDateString() : 
                      'Unknown date';
                    const procedureName = procedure.code?.text || 
                      procedure.code?.coding?.[0]?.display || 
                      'Unknown procedure';
                    const procedureStatus = procedure.status || 'Unknown';
                    const performer = procedure.performer?.[0]?.actor?.display || 'Unknown performer';
                    const location = procedure.location?.display || 'Unknown location';
                    
                    return (
                      <div key={`procedure-${procedure.id || index}`} className="p-4 bg-orange-50 rounded-lg border border-orange-200 mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{procedureName}</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            procedureStatus === 'completed' ? 'bg-green-100 text-green-800' :
                            procedureStatus === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                            procedureStatus === 'preparation' ? 'bg-blue-100 text-blue-800' :
                            procedureStatus === 'not-done' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {procedureStatus}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">Performer: {performer}</p>
                        <p className="text-sm text-gray-600 mb-1">Location: {location}</p>
                        <p className="text-sm font-semibold text-gray-900">Date: {procedureDate}</p>
                        {procedure.reasonCode?.[0]?.text && (
                          <p className="text-xs text-gray-500 mt-1">Reason: {procedure.reasonCode[0].text}</p>
                        )}
                      </div>
                    );
                  })}

                  {/* Display Service Requests (Orders) */}
                  {serviceRequests.map((serviceRequest, index) => {
                    const requestDate = serviceRequest.authoredOn ? 
                      new Date(serviceRequest.authoredOn).toLocaleDateString() : 
                      'Unknown date';
                    const requestName = serviceRequest.code?.text || 
                      serviceRequest.code?.coding?.[0]?.display || 
                      'Unknown order';
                    const requestStatus = serviceRequest.status || 'Unknown';
                    const requester = serviceRequest.requester?.display || 'Unknown requester';
                    const category = serviceRequest.category?.[0]?.coding?.[0]?.display || 'Unknown category';
                    
                    return (
                      <div key={`service-${serviceRequest.id || index}`} className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{requestName}</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            requestStatus === 'active' ? 'bg-green-100 text-green-800' :
                            requestStatus === 'completed' ? 'bg-blue-100 text-blue-800' :
                            requestStatus === 'revoked' ? 'bg-red-100 text-red-800' :
                            requestStatus === 'draft' ? 'bg-gray-100 text-gray-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {requestStatus}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">Category: {category}</p>
                        <p className="text-sm text-gray-600 mb-1">Requester: {requester}</p>
                        <p className="text-sm font-semibold text-gray-900">Date: {requestDate}</p>
                        {serviceRequest.reasonCode?.[0]?.text && (
                          <p className="text-xs text-gray-500 mt-1">Reason: {serviceRequest.reasonCode[0].text}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <HeartIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No procedures or orders found</p>
                </div>
              )}
            </div>
          </div>

          {/* Vaccinations Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center mb-6">
              <div className="bg-teal-100 p-3 rounded-full mr-3">
                <ShieldCheckIcon className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Immunizations</h3>
            </div>
            <div className="space-y-3">
              {loadingImmunizations ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                  <span className="ml-2 text-gray-600">Loading immunizations...</span>
                </div>
              ) : immunizations.length > 0 ? (
                <div className="max-h-96 overflow-y-auto pr-2">
                  {immunizations.map((immunization, index) => {
                    const immunizationDate = immunization.occurrenceDateTime ? 
                      new Date(immunization.occurrenceDateTime).toLocaleDateString() : 
                      immunization.occurrenceString || 'Unknown date';
                    const immunizationName = immunization.vaccineCode?.text || 
                      immunization.vaccineCode?.coding?.[0]?.display || 
                      'Unknown vaccine';
                    const immunizationStatus = immunization.status || 'Unknown';
                    const lotNumber = immunization.lotNumber || 'No lot number';
                    const manufacturer = immunization.manufacturer?.display || 'Unknown manufacturer';
                    
                    return (
                      <div key={immunization.id || index} className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{immunizationName}</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            immunizationStatus === 'completed' ? 'bg-green-100 text-green-800' :
                            immunizationStatus === 'entered-in-error' ? 'bg-red-100 text-red-800' :
                            immunizationStatus === 'not-done' ? 'bg-gray-100 text-gray-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {immunizationStatus}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">Date: {immunizationDate}</p>
                        <p className="text-sm text-gray-600 mb-1">Lot: {lotNumber}</p>
                        <p className="text-sm text-gray-600">Manufacturer: {manufacturer}</p>
                        {immunization.reasonCode?.[0]?.text && (
                          <p className="text-xs text-gray-500 mt-1">Reason: {immunization.reasonCode[0].text}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ShieldCheckIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No immunizations found</p>
                </div>
              )}
            </div>
          </div>

          {/* Vitals Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center mb-6">
              <div className="bg-red-100 p-3 rounded-full mr-3">
                <HeartIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Vital Signs</h3>
            </div>
            <div className="space-y-3">
              {loadingVitalSigns ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                  <span className="ml-2 text-gray-600">Loading vital signs...</span>
                </div>
              ) : vitalSigns.length > 0 ? (
                <div className="max-h-96 overflow-y-auto pr-2">
                  {vitalSigns.map((observation, index) => {
                    const vitalName = observation.code?.text || 
                      observation.code?.coding?.[0]?.display || 
                      'Unknown vital';
                    const vitalValue = observation.valueQuantity?.value || 
                      observation.valueString || 
                      observation.valueCodeableConcept?.text || 
                      'No value';
                    const vitalUnit = observation.valueQuantity?.unit || '';
                    const vitalDate = observation.effectiveDateTime ? 
                      new Date(observation.effectiveDateTime).toLocaleDateString() : 
                      'Unknown date';
                    const vitalTime = observation.effectiveDateTime ? 
                      new Date(observation.effectiveDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                      '';
                    const vitalStatus = observation.status || 'Unknown';
                    
                    // Check if value is abnormal based on reference ranges
                    const isAbnormal = observation.referenceRange?.some(range => {
                      if (range.low && range.high) {
                        return vitalValue < range.low.value || vitalValue > range.high.value;
                      } else if (range.low) {
                        return vitalValue < range.low.value;
                      } else if (range.high) {
                        return vitalValue > range.high.value;
                      }
                      return false;
                    }) || false;

                    // Get reference range text
                    const referenceRange = observation.referenceRange?.[0]?.text || 
                      (observation.referenceRange?.[0]?.low && observation.referenceRange?.[0]?.high ? 
                        `${observation.referenceRange[0].low.value} - ${observation.referenceRange[0].high.value} ${vitalUnit}` : 
                        'No reference range');

                    return (
                      <div key={observation.id || index} className={`p-4 rounded-lg border mb-3 ${
                        isAbnormal ? 'bg-red-50 border-red-200' : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{vitalName}</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            isAbnormal ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {isAbnormal ? 'Abnormal' : 'Normal'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <div>
                            <p className="text-lg font-semibold text-gray-900">
                              {vitalValue} {vitalUnit}
                            </p>
                            <p className="text-sm text-gray-600">Reference: {referenceRange}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Date: {vitalDate}</p>
                            <p className="text-sm text-gray-600">Time: {vitalTime}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            vitalStatus === 'final' ? 'bg-green-100 text-green-800' :
                            vitalStatus === 'preliminary' ? 'bg-yellow-100 text-yellow-800' :
                            vitalStatus === 'amended' ? 'bg-blue-100 text-blue-800' :
                            vitalStatus === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {vitalStatus}
                          </span>
                          {observation.note?.[0]?.text && (
                            <p className="text-xs text-gray-500">Note: {observation.note[0].text}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <HeartIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No vital signs found</p>
                </div>
              )}
            </div>
          </div>

          {/* Labs Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center mb-6">
              <div className="bg-indigo-100 p-3 rounded-full mr-3">
                <DocumentMagnifyingGlassIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Laboratory Results</h3>
            </div>
            <div className="space-y-3">
              {loadingLabResults ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  <span className="ml-2 text-gray-600">Loading lab results...</span>
                </div>
              ) : labResults.length > 0 ? (
                <div className="max-h-96 overflow-y-auto pr-2">
                  {labResults.map((observation, index) => {
                    const labName = observation.code?.text || 
                      observation.code?.coding?.[0]?.display || 
                      'Unknown lab test';
                    const labValue = observation.valueQuantity?.value || 
                      observation.valueString || 
                      observation.valueCodeableConcept?.text || 
                      'No value';
                    const labUnit = observation.valueQuantity?.unit || '';
                    const labDate = observation.effectiveDateTime ? 
                      new Date(observation.effectiveDateTime).toLocaleDateString() : 
                      'Unknown date';
                    const labTime = observation.effectiveDateTime ? 
                      new Date(observation.effectiveDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                      '';
                    const labStatus = observation.status || 'Unknown';
                    
                    // Check if value is abnormal based on reference ranges
                    const isAbnormal = observation.referenceRange?.some(range => {
                      if (range.low && range.high) {
                        return labValue < range.low.value || labValue > range.high.value;
                      } else if (range.low) {
                        return labValue < range.low.value;
                      } else if (range.high) {
                        return labValue > range.high.value;
                      }
                      return false;
                    }) || false;

                    // Get reference range text
                    const referenceRange = observation.referenceRange?.[0]?.text || 
                      (observation.referenceRange?.[0]?.low && observation.referenceRange?.[0]?.high ? 
                        `${observation.referenceRange[0].low.value} - ${observation.referenceRange[0].high.value} ${labUnit}` : 
                        'No reference range');

                    // Get lab category/panel
                    const labCategory = observation.category?.[0]?.coding?.[0]?.display || 
                      observation.category?.[0]?.text || 
                      'Laboratory';

                    return (
                      <div key={observation.id || index} className={`p-4 rounded-lg border mb-3 ${
                        isAbnormal ? 'bg-red-50 border-red-200' : 'bg-indigo-50 border-indigo-200'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <h4 className="font-semibold text-gray-900">{labName}</h4>
                            {isAbnormal && (
                              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Abnormal
                              </span>
                            )}
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            labStatus === 'final' ? 'bg-green-100 text-green-800' :
                            labStatus === 'preliminary' ? 'bg-yellow-100 text-yellow-800' :
                            labStatus === 'amended' ? 'bg-blue-100 text-blue-800' :
                            labStatus === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {labStatus}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <div>
                            <p className="text-lg font-semibold text-gray-900">
                              {labValue} {labUnit}
                            </p>
                            <p className="text-sm text-gray-600">Reference: {referenceRange}</p>
                            <p className="text-xs text-gray-500">Category: {labCategory}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Date: {labDate}</p>
                            <p className="text-sm text-gray-600">Time: {labTime}</p>
                          </div>
                        </div>
                        {observation.note?.[0]?.text && (
                          <p className="text-xs text-gray-500 mt-1">Note: {observation.note[0].text}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DocumentMagnifyingGlassIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No laboratory results found</p>
                </div>
              )}
            </div>
          </div>


        </div>
      </div>

      {/* Appointment Booking Modal */}
      <AppointmentBookingModal
        isOpen={showAppointmentModal}
        onClose={() => setShowAppointmentModal(false)}
        patientId={patientId}
      />

      {/* Encounter Documents Modal */}
      <EncounterDocumentsModal
        isOpen={showEncounterDocsModal}
        onClose={() => setShowEncounterDocsModal(false)}
        encounter={selectedEncounter}
        patientId={patientId}
      />
    </div>
  );
};

export default PatientDashboard; 